import * as React from 'react';
import { findHighestRole, RoleType, UserModel } from '../models/User';
import { API_URL, ApiResponse, getHeaders } from '../lib/_api-helpers';
import { Organisation } from 'models/Organisation';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';

const USER_POOL_ID = process.env.REACT_APP_AUTH_USER_POOL_ID;
const USER_POOL_CLIENT_ID = process.env.REACT_APP_AUTH_USER_POOL_CLIENT_ID;

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: USER_POOL_ID!,
            userPoolClientId: USER_POOL_CLIENT_ID!,
            signUpVerificationMethod: 'code',
            passwordFormat: {
                minLength: 8,
                requireLowercase: true,
                requireUppercase: true,
                requireNumbers: true,
                requireSpecialCharacters: true,
            },
        },
    },
});

export type UserData = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: RoleType[];
    activeRole: RoleType | '';
    organisation: Organisation | null;
};

type AuthState = {
    userData: UserData;
    isAuthenticated: boolean;
    state: string;
    setState: React.Dispatch<React.SetStateAction<string>>;
    refreshUserData: () => Promise<void>;
    setActiveRole: (newRole: RoleType) => void;
    signOutNeve: () => Promise<void>;
};

export const AuthContext = React.createContext<AuthState>({
    userData: {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        roles: [],
        activeRole: '',
        organisation: null,
    },
    isAuthenticated: false,
    state: '',
    setState: () => {},
    refreshUserData: async () => {},
    setActiveRole: (_: RoleType) => {},
    signOutNeve: async () => {},
});

export function AuthProvider(props: { children: React.ReactNode | React.ReactNode[] }): JSX.Element {
    const [isInitialising, setIsInitalising] = React.useState<boolean>(true);
    const [userData, setUserData] = React.useState<UserData>();
    const [state, setState] = React.useState<string>('');

    React.useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        setState(searchParams.get('state') ?? '');

        const checkIfAuthenticated = async () => {
            try {
                const session = await fetchAuthSession();
                const accessToken = session.tokens?.accessToken?.toString();
                if (!accessToken) {
                    return;
                }

                await populateUserData(accessToken);
            } catch (error: any) {
                console.log('AuthProvider checkIfAuthenticated', error);
            } finally {
                setIsInitalising(false); //must be inside finally
            }
        };

        checkIfAuthenticated();
    }, []); //eslint-disable-line

    if (isInitialising) return <></>;

    function setActiveRole(newRole: RoleType) {
        setUserData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                activeRole: newRole,
            };
        });
    }

    async function populateUserData(accessToken: string) {
        const user = await getNeveUser(accessToken);
        //persist current active role if already signed in
        const activeRole = userData?.activeRole ?? findHighestRole(user.roles.map((r) => r.name));

        const newUserData: UserData = {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            roles: user.roles.map((x) => x.name),
            activeRole: activeRole,
            organisation: user.organisation,
        };

        setUserData(newUserData);
    }

    async function refreshUserData(): Promise<void> {
        const session = await fetchAuthSession();
        const token = session.tokens!.accessToken.toString();
        await populateUserData(token);
    }

    async function signOutNeve(): Promise<void> {
        await signOut();
        setUserData(undefined);
    }

    const isAuthenticated = !!userData;

    const authState: AuthState = {
        userData: userData!,
        isAuthenticated: isAuthenticated,
        state: state,
        setState: setState,
        setActiveRole: setActiveRole,
        refreshUserData: refreshUserData,
        signOutNeve: signOutNeve,
    };

    return <AuthContext.Provider value={authState}>{props.children}</AuthContext.Provider>;
}

async function getNeveUser(token: string): Promise<UserModel> {
    const headers = getHeaders(token);

    let user: UserModel;

    const userResponse = await fetch(`${API_URL}/users/me`, { headers });

    if (userResponse.status === 404) {
        throw new Error('User could not be found.');
    } else {
        const userResponseJson: ApiResponse<UserModel> = await userResponse.json();

        if (!userResponseJson.value) {
            throw new Error('User could not be found.');
        }

        user = userResponseJson.value;
    }

    return user;
}
